import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaSave, FaTimes, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getProducts, getCategories, updateProduct, deleteProduct as deleteProductApi } from '../../config/back4app';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Función para obtener categorías y productos desde el backend
  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Obtener las categorías primero
      const categoriesData = await getCategories();
      setCategories(categoriesData);
      
      // Luego obtener todos los productos (incluyendo inactivos)
      const productsData = await getProducts(null, true);
      
      // Mapear los productos para asegurarnos de tener toda la información necesaria
      const enhancedProducts = productsData.map(product => ({
        id: product.id,
        name: product.name || 'Sin nombre',
        categoryId: product.category?.id,
        categoryName: product.category?.name || 'Sin categoría',
        presentation: product.presentation || '',
        content: product.content || '',
        price: product.price || 0,
        description: product.description || '',
        moq: product.moq || 0,
        status: product.status || 'active',
        countPerBottle: product.countPerBottle || [],
        image: product.image
      }));
      
      console.log('Productos cargados:', enhancedProducts);
      setProducts(enhancedProducts);
      setError(null);
    } catch (err) {
      console.error('Error al cargar productos:', err);
      setError('Error al cargar los productos: ' + err.message);
      toast.error('No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);
  
  // Eliminar un producto
  const deleteProduct = async (productId) => {
    try {
      await deleteProductApi(productId);
      setProducts(products.filter(product => product.id !== productId));
      setConfirmDelete(null);
      toast.success('Producto eliminado con éxito');
    } catch (err) {
      console.error('Error eliminando producto:', err);
      toast.error('Error al eliminar el producto: ' + err.message);
    }
  };
  
  // Iniciar la edición de un producto
  const startEditing = (product) => {
    setEditingProduct(product.id);
    setEditForm({
      name: product.name,
      price: product.price,
      presentation: product.presentation,
      categoryId: product.categoryId,
      status: product.status,
      moq: product.moq || 0
    });
  };
  
  // Cancelar la edición
  const cancelEditing = () => {
    setEditingProduct(null);
    setEditForm({});
  };
  
  // Guardar cambios de la edición
  const saveProductChanges = async (productId) => {
    try {
      const productToUpdate = products.find(p => p.id === productId);
      if (!productToUpdate) return;
      
      const updatedProduct = {
        ...productToUpdate,
        ...editForm
      };
      
      await updateProduct(productId, updatedProduct);
      
      // Actualizar la lista local
      setProducts(products.map(p => 
        p.id === productId ? { ...p, ...editForm } : p
      ));
      
      setEditingProduct(null);
      setEditForm({});
      toast.success('Producto actualizado con éxito');
    } catch (err) {
      console.error('Error actualizando producto:', err);
      toast.error('Error al actualizar el producto: ' + err.message);
    }
  };
  
  // Manejar cambios en el formulario
  const handleEditFormChange = (field, value) => {
    setEditForm({
      ...editForm,
      [field]: value
    });
  };
  
  // Filtrar productos según el término de búsqueda
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      (product.name && product.name.toLowerCase().includes(term)) ||
      (product.categoryName && product.categoryName.toLowerCase().includes(term)) ||
      (product.description && product.description.toLowerCase().includes(term)) ||
      (product.presentation && product.presentation.toLowerCase().includes(term))
    );
  });
  
  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Productos</h1>
        <Link to="/admin/products/new" className="btn btn-primary">
          <FaPlus className="me-2" />
          Nuevo Producto
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <div className="row align-items-center">
            <div className="col">
              <div className="input-group">
                <span className="input-group-text">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre o categoría..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="d-flex justify-content-center my-5">
              <p>Cargando...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover border">
                <thead className="table-light">
                  <tr>
                    <th style={{width: '35%'}}>Nombre</th>
                    <th style={{width: '15%'}}>Categoría</th>
                    <th style={{width: '15%'}}>Presentación</th>
                    <th style={{width: '10%'}}>Precio</th>
                    <th style={{width: '10%'}}>Estado</th>
                    <th style={{width: '15%'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        {searchTerm ? 'No se encontraron productos que coincidan con la búsqueda' : 'No hay productos registrados'}
                      </td>
                    </tr>
                  ) : (
                    // Imprimiendo información de depuración
                    console.log('Rendering products table', filteredProducts) || filteredProducts.map(product => (
                      <tr key={product.id}>
                        {/* COLUMNA NOMBRE + IMAGEN */}
                        <td className="align-middle">
                          {editingProduct === product.id ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editForm.name || ''}
                              onChange={(e) => handleEditFormChange('name', e.target.value)}
                            />
                          ) : (
                            <div className="d-flex align-items-center">
                              <img 
                                src={product.image || 'https://via.placeholder.com/50'} 
                                alt={product.name} 
                                className="rounded me-2" 
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }} 
                              />
                              <span>{product.name || `Producto ${product.id.substring(0, 6)}`}</span>
                            </div>
                          )}
                        </td>
                        
                        {/* COLUMNA CATEGORÍA */}
                        <td className="align-middle">
                          {editingProduct === product.id ? (
                            <select
                              className="form-select form-select-sm"
                              value={editForm.categoryId || ''}
                              onChange={(e) => handleEditFormChange('categoryId', e.target.value)}
                            >
                              <option value="">Seleccionar categoría</option>
                              {categories.map(category => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                              ))}
                            </select>
                          ) : (
                            product.categoryName || 'Sin categoría'
                          )}
                        </td>
                        
                        {/* COLUMNA PRESENTACIÓN */}
                        <td className="align-middle">
                          {editingProduct === product.id ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editForm.presentation || ''}
                              onChange={(e) => handleEditFormChange('presentation', e.target.value)}
                            />
                          ) : (
                            product.presentation || '-'
                          )}
                        </td>
                        
                        {/* COLUMNA PRECIO */}
                        <td className="align-middle text-end">
                          {editingProduct === product.id ? (
                            <input
                              type="number"
                              step="0.01"
                              className="form-control form-control-sm"
                              value={editForm.price || 0}
                              onChange={(e) => handleEditFormChange('price', parseFloat(e.target.value))}
                            />
                          ) : (
                            product.price ? `$${product.price.toFixed(2)}` : '$0.00'
                          )}
                        </td>
                        
                        {/* COLUMNA ESTADO */}
                        <td className="align-middle text-center">
                          {editingProduct === product.id ? (
                            <select
                              className="form-select form-select-sm"
                              value={editForm.status || 'active'}
                              onChange={(e) => handleEditFormChange('status', e.target.value)}
                            >
                              <option value="active">Activo</option>
                              <option value="inactive">Inactivo</option>
                            </select>
                          ) : (
                            <span className={`badge ${product.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                              {product.status === 'active' ? 'Activo' : 'Inactivo'}
                            </span>
                          )}
                        </td>
                        <td className="align-middle">
                          <div className="d-flex justify-content-center">
                            {editingProduct === product.id ? (
                              <>
                                <button 
                                  className="btn btn-sm btn-success me-2"
                                  onClick={() => saveProductChanges(product.id)}
                                >
                                  <FaSave />
                                </button>
                                <button 
                                  className="btn btn-sm btn-secondary"
                                  onClick={cancelEditing}
                                >
                                  <FaTimes />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn btn-sm btn-primary me-2"
                                  onClick={() => startEditing(product)}
                                >
                                  <FaEdit />
                                </button>
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => setConfirmDelete(product.id)}
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación para eliminar - simplificado sin transiciones */}
      {confirmDelete && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar eliminación</h5>
                <button type="button" className="btn-close" onClick={() => setConfirmDelete(null)}></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancelar</button>
                <button type="button" className="btn btn-danger" onClick={() => deleteProduct(confirmDelete)}>Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
